import React from "react";
import { CheckCircle, AlertTriangle, XCircle, Info } from "lucide-react";

function ImportResultsDialog({ open, onClose, results }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Info className="w-5 h-5 text-gray-500" />
            Import Results
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-2xl"
          >
            &times;
          </button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="font-semibold">
                {results?.insertedCount} Success
              </span>
            </div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <span className="font-semibold">
                {results?.conflictCount} Conflicts
              </span>
            </div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-600" />
              <span className="font-semibold">
                {results?.skippedEntries} Skipped
              </span>
            </div>
          </div>
        </div>

        {/* Progress Visualization */}
        <div className="h-2 bg-gray-200 rounded-full mb-6">
          <div
            className="h-full rounded-full bg-green-500 transition-all"
            style={{
              width: `${
                (results?.insertedCount / results?.totalProcessed) * 100
              }%`,
            }}
          />
        </div>

        {/* Conflict Details */}
        {results?.conflictCount > 0 && (
          <div className="mb-6">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-600" />
              Conflicts Detected ({results?.conflictCount})
            </h3>
            <div className="border rounded-lg overflow-hidden">
              {results?.sampleConflicts.map((conflict, idx) => (
                <div key={idx} className="p-3 border-b last:border-b-0">
                  <div className="text-sm">
                    <span className="font-medium">User:</span> {conflict?.user}
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">Location:</span>{" "}
                    {conflict?.location}
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">Time:</span>{" "}
                    {conflict?.timeRange}
                  </div>
                </div>
              ))}
              {results.conflictCount > 5 && (
                <div className="p-3 bg-gray-50 text-sm text-gray-500">
                  + {results.conflictCount - 5} more conflicts...
                </div>
              )}
            </div>
          </div>
        )}

        {/* Skipped Entries */}
        {results?.skippedEntries > 0 && (
          <div className="mb-6">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-600" />
              Skipped Entries ({results?.skippedEntries})
            </h3>
            <p className="text-sm text-gray-600">
              Entries skipped due to missing/invalid data. Check file
              formatting.
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default ImportResultsDialog;
